import React, {useState, useRef, useMemo} from "react";
import GoogleMapReact from "google-map-react";
import {Polyline} from "@react-google-maps/api";
import {getGoogleMapBootstrapURLKeys} from "../../constants/GoogleMaps";
import {driveTimeForLeg, legMidpoint} from "../../constants/DriveTime";

const PinLocation = ({text, color}) => {
	return (
		<div style={{width: "300px"}}>
			<p style={{fontWeight: "bolder", color: color, fontSize: "20px"}}>
				{text}
			</p>
		</div>
	);
};

const DriveTimeLabel = ({text, color}) => (
	<div style={{
		transform: "translate(-50%, -50%)",
		background: "rgba(255,255,255,0.92)",
		border: `1px solid ${color}`,
		borderRadius: 4,
		padding: "1px 5px",
		fontSize: 11,
		fontWeight: 700,
		color,
		whiteSpace: "nowrap",
		pointerEvents: "none",
		boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
	}}>
		{text}
	</div>
);

const StopNumberMarker = ({number, color}) => (
	<div style={{
		transform: "translate(-50%, -50%)",
		width: 22,
		height: 22,
		borderRadius: "50%",
		background: color,
		color: "#fff",
		border: "2px solid #000",
		fontSize: 11,
		fontWeight: 700,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		pointerEvents: "none",
		boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
	}}>
		{number}
	</div>
);

const StaticDispatchMap = ({schedule, region, userColors}) => {
	const [map, setMap] = useState();
	const [maps, setMaps] = useState();
	const mapRef = useRef();

	const {markers, lines, driveLabels, stopMarkers} = useMemo(() => {
		let markers = [];
		let lines = [];
		let driveLabels = [];
		let stopMarkers = [];

		Object.entries(schedule).forEach(([engineerId, engineerData]) => {
			const userColor = userColors[engineerId];
			const {lat: engLat, lng: engLng, fe_name, appointments} = engineerData;
			if (!engLat || !engLng) return;

			// Home + tiny offset keeps house icon oriented at 0deg
			let userCoords = [{lat: parseFloat(engLat), lng: parseFloat(engLng)}, {lat: parseFloat(engLat) + .0000001, lng: parseFloat(engLng)}];
			appointments.forEach(appt => {
				const lat = parseFloat(appt.lat);
				const lng = parseFloat(appt.lng);
				if (!isNaN(lat) && !isNaN(lng) && !['Blank', 'Blocked'].includes(appt.pkey) && !appt.is_remote) {
					userCoords.push({lat, lng});
				}
			});

			const houseSymbol = {
				path: "M -3.5437 0 V 4.3312 H -1.1813 V 1.575 H 1.1813 V 4.3312 H 3.5437 V 0 L 0 -2.7563 Z",
				strokeColor: "#000000",
				fillColor: userColor,
				fillOpacity: 1,
			};

			const paths = [];
			userCoords.forEach((coord, index) => {
				if (index > 0) {
					paths.push([
						{lat: parseFloat(userCoords[index - 1].lat), lng: parseFloat(userCoords[index - 1].lng)},
						{lat: parseFloat(coord.lat), lng: parseFloat(coord.lng)},
					]);
				}
			});

			paths.forEach((path, index) => {
				const isDummy = index === 0;
				lines.push(
					<Polyline
						key={`${engineerId}-${index}`}
						path={path}
						options={{
							map,
							strokeColor: userColor,
							strokeOpacity: 1,
							strokeWeight: 4,
							icons: isDummy
								? [{
									icon: {...houseSymbol, rotation: 0},
									offset: "0%",
									fixedRotation: true,
								}]
								: [],
						}}
					/>
				);

				if (!isDummy) {
					const stopNumber = index;
					const midpoint = legMidpoint(path[0], path[1]);
					const {drive_time} = driveTimeForLeg(path[0], path[1]);

					stopMarkers.push(
						<StopNumberMarker
							key={`stop-${engineerId}-${stopNumber}`}
							lat={path[1].lat}
							lng={path[1].lng}
							number={stopNumber}
							color={userColor}
						/>
					);

					if (drive_time > 0) {
						driveLabels.push(
							<DriveTimeLabel
								key={`drive-${engineerId}-${index}`}
								lat={midpoint.lat}
								lng={midpoint.lng}
								text={`${drive_time} min`}
								color={userColor}
							/>
						);
					}
				}
			});

			if (userCoords.length) {
				const last = userCoords[userCoords.length - 1];
				markers.push(
					<PinLocation
						key={`engineer-${engineerId}`}
						zoom={9}
						lat={last.lat}
						lng={last.lng}
						text={fe_name}
						color={userColor}
					/>
				);
			}
		});

		return {markers, lines, driveLabels, stopMarkers};
	}, [schedule, map, maps, userColors]);

	const center = {
		lat: parseFloat(region.latitude),
		lng: parseFloat(region.longitude),
	};

	return (
		<div style={{height: "50vh", width: "100%"}}>
			<GoogleMapReact
				bootstrapURLKeys={getGoogleMapBootstrapURLKeys()}
				defaultCenter={center}
				defaultZoom={10}
				yesIWantToUseGoogleMapApiInternals
				onGoogleApiLoaded={({map, maps}) => {
					setMap(map);
					setMaps(maps);
				}}
				ref={mapRef}
				center={center}
				zoom={10}
			>
				{markers}
				{lines}
				{stopMarkers}
				{driveLabels}
			</GoogleMapReact>
		</div>
	);
};

export default StaticDispatchMap;
